import { getMostRecentActivity } from "@/lib/utils";
import { ValCallResult } from "@/models/pipeline";

export const naturalSort = (a: string, b: string): number => {
  const regex = /(\d+)|(\D+)/g;
  const aParts = a.match(regex) || [];
  const bParts = b.match(regex) || [];

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = aParts[i] || "";
    const bPart = bParts[i] || "";

    const aNum = parseInt(aPart, 10);
    const bNum = parseInt(bPart, 10);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) return aNum - bNum;
    } else {
      const cmp = aPart.localeCompare(bPart);
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getNestedValue = (obj: any, path: string): unknown => {
   return path.split(".").reduce((acc, part) => acc?.[part], obj);
 };

export const sortPipelineData = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[],
  sortBy: string,
  sortOrder: "asc" | "desc"
) => {
  return [...data].sort((a, b) => {
    const multiplier = sortOrder === "asc" ? 1 : -1;
    let aValue = sortBy.includes(".") ? getNestedValue(a, sortBy) : a[sortBy];
    let bValue = sortBy.includes(".") ? getNestedValue(b, sortBy) : b[sortBy];

    if (sortBy === "newActivity") {
      const aMostRecentActivity = getMostRecentActivity(a.activities || [], a.lastTouch);
      const bMostRecentActivity = getMostRecentActivity(b.activities || [], b.lastTouch);

      if (!aMostRecentActivity && !bMostRecentActivity) return 0;
      if (!aMostRecentActivity) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (!bMostRecentActivity) {
        return sortOrder === "asc" ? 1 : -1;
      }

      return (
        multiplier *
        (new Date(bMostRecentActivity.activityTime).getTime() - new Date(aMostRecentActivity.activityTime).getTime())
      );
    }

    if (sortBy === "decision") {
      aValue = a.quoteStatus;
      bValue = b.quoteStatus;
    }

    if (sortBy === "quotes") {
      aValue = a.quotesTotalCount;
      bValue = b.quotesTotalCount;
    }

    if (sortBy === "status") {
      aValue = String(aValue || "");
      bValue = String(bValue || "");
      return multiplier * aValue.localeCompare(bValue);
    }

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return -1 * multiplier;
    if (bValue === null) return 1 * multiplier;
    if (aValue === "-" && bValue === "-") return 0;
    if (aValue === "-") return -1 * multiplier;
    if (bValue === "-") return 1 * multiplier;
    if (aValue === "N/A" && bValue === "N/A") return 0;
    if (aValue === "N/A") return -1 * multiplier;
    if (bValue === "N/A") return 1 * multiplier;

    if (sortBy === "lastTouch") {
      return multiplier * (new Date(bValue).getTime() - new Date(aValue).getTime());
    }

    if (sortBy === "allocatedOn") {
      return multiplier * (new Date(bValue).getTime() - new Date(aValue).getTime());
    }

    if (sortBy === "paidOutDate") {
      return multiplier * (new Date(aValue).getTime() - new Date(bValue).getTime());
    }

    if (
      sortBy === "dealerValCallResult" ||
      sortBy === "customerValCall1Result" ||
      sortBy === "customerValCall2Result"
    ) {
      const getValCallSortValue = (value: ValCallResult) => {
        switch (value) {
          case ValCallResult.Completed:
            return 2;
          case ValCallResult.NotCompleted:
            return 1;
          case ValCallResult.NotRequired:
            return 0;
          default:
            return -1;
        }
      };

      return multiplier * (getValCallSortValue(aValue) - getValCallSortValue(bValue));
    }

    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      if (aValue === bValue) return 0;
      return multiplier * (aValue ? 1 : -1);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return multiplier * (aValue - bValue);
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return multiplier * aValue.localeCompare(bValue);
    }

    return 0;
  });
};
